import { StandardSnack } from '@components/snackbar';
import { BlockEditor } from '@sketchnote/block-editor';
import { CloseIcon } from '@sketchnote/icons';
import { Button } from '@sketchnote/shared-components';
import { LoaderXs } from '@sketchnote/shared-components';
import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import * as React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import InfiniteScroll from 'react-infinite-scroll-component';
import { API } from 'src/api';
import { PageErrorBoundary } from 'src/components/error-boundary/page-error-boundary';

import useCollaborativeEditorStore from './liveblocks/collaborativeEditorStore';

const { useState, useEffect, useMemo } = React;

export const VersionHistory = ({
  docId,
  onClose,
  onApply,
}: {
  docId: string;
  onClose?: () => void;
  onApply: () => void;
}) => {
  const [versionData, setVersionData] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [currentSelectedVersion, setCurrentSelectedVersion] = useState();
  const { onRestoreVersion, setVersionRestorer } = useCollaborativeEditorStore();
  const getVersionData = async (page, reset = false) => {
    const data = await API.get(`/creation/version?id=${docId}&page=${page}&limit=10`);

    if (data?.data?.success) {
      setVersionData((prev) => {
        if (prev.length === 0 || reset) {
          prev = data?.data?.data;
        } else {
          prev.push(...data?.data?.data);
          prev = prev.map((x) => x);
        }
        return prev;
      });
      if (data?.data?.data.length === 0) {
        setHasMore(false);
      }
    }
  };

  const handleFetchVersions = () => {
    getVersionData(page + 1);
    setPage(page + 1);
  };

  useEffect(() => {
    getVersionData(0);
  }, []);

  const currentVersionIndex = useMemo(() => {
    if (versionData.length === 0) {
      return 0;
    }
    return versionData.findIndex((x) => x.creationVersionId === currentSelectedVersion);
  }, [currentSelectedVersion, versionData]);
  return (
    <div className="flex bg-white rounded-lg " style={{ height: '70vh' }}>
      <div
        style={{ width: '22.4rem' }}
        id="scrollableDiv"
        className="bg-gray-100 px-20 py-24 rounded-l-lg overflow-auto"
      >
        <h3 className="font-medium leading-32 text-base mb-24 ">Version History</h3>
        <nav className="flex flex-col">
          {versionData.length > 0 ? (
            <InfiniteScroll
              dataLength={versionData.length}
              next={handleFetchVersions}
              hasMore={hasMore}
              scrollableTarget="scrollableDiv"
              loader={
                <div className="overflow-hidden">
                  <LoaderXs className="mx-auto text-xss m-10" />
                </div>
              }
              endMessage={
                <div className="flex items-center justify-center mt-5">
                  <StandardSnack text="Done!" />
                </div>
              }
            >
              {versionData.map((version) => (
                <div
                  role="button"
                  tabIndex={0}
                  key={version.creationVersionId}
                  onClick={() => {
                    setCurrentSelectedVersion(version.creationVersionId);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setCurrentSelectedVersion(version.creationVersionId);
                    }
                  }}
                  className={cn({
                    'px-8 py-7 flex text-gray-999 items-center border-transparent border focus:border-primary-500 hover:bg-primary-200 rounded-sm min-w-btn-lg cursor-pointer align-middle text-sm font-medium':
                      true,
                    'bg-primary-100': version.creationVersionId === currentSelectedVersion,
                  })}
                >
                  <img
                    height="24px"
                    width="24px"
                    className="rounded-full h-24 w-24 object-center object-cover mr-8"
                    src={
                      version?.updatedBy?.avatar ??
                      'https://www.kindpng.com/picc/m/24-248253_user-profile-default-image-png-clipart-png-download.png'
                    }
                  />
                  <div className="flex flex-col">
                    <span
                      className={cn('text-sm font-medium', {
                        'text-primary-500': version.creationVersionId === currentSelectedVersion,
                      })}
                    >
                      {version?.updatedBy?.firstname ?? 'User'}
                    </span>
                    <span className="text-xss">
                      {format(parseISO(version.versionDate), 'MMM dd hh:mm aaa')}
                    </span>
                  </div>
                </div>
              ))}
            </InfiniteScroll>
          ) : null}
        </nav>
      </div>

      <div className="bg-white border-r rounded-lg">
        <div className="flex justify-end h-48 items-center px-20">
          <button
            aria-label="Close icon"
            className="text-gray-500 text-xs px-5 pt-3 rounded-sm hover:bg-gray-100"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
        <div
          className="px-48 overflow-auto"
          style={{ width: `calc(${900}px - 224px)`, height: `calc(100% - 108px)` }}
        >
          {currentSelectedVersion && (
            <PageErrorBoundary wrapperClass="mt-16" errorPage="versionHistory">
              <DndProvider backend={HTML5Backend}>
                <BlockEditor
                  isReadOnly={true}
                  id={versionData[currentVersionIndex]?.creationVersionId}
                  value={
                    Array.isArray(versionData[currentVersionIndex]?.document)
                      ? versionData[currentVersionIndex]?.document
                      : versionData[currentVersionIndex]?.document?.data
                  }
                />
              </DndProvider>
            </PageErrorBoundary>
          )}
        </div>
        <div className="border-t flex justify-end px-16 py-12">
          <Button status="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            status="primary"
            className="ml-10"
            onClick={() => {
              if (versionData[currentVersionIndex]?.creationVersionId && onRestoreVersion) {
                if (Array.isArray(versionData[currentVersionIndex]?.document)) {
                  onRestoreVersion(versionData[currentVersionIndex]?.document);
                } else {
                  onRestoreVersion(versionData[currentVersionIndex]?.document?.data);
                }
                setVersionRestorer(null);
                onApply();
              }
            }}
          >
            Restore version
          </Button>
        </div>
      </div>
    </div>
  );
};
